import React, { useState, useEffect } from 'react';
import {
  UserCircle, PencilSimple, LockSimple, Power, Plus, MagnifyingGlass, X, EnvelopeSimple
} from '@phosphor-icons/react/ssr';
import { api } from './services/apiClient';

const ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'user',  label: 'Usuario' },
];

const UsersModule = ({
  useAuth,
  useNotification,
  BaseButton,
  BaseInput,
  BaseCard,
}) => {
  const { user: currentUser } = useAuth();
  const { addNotification }   = useNotification();

  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [users, setUsers]         = useState([]);
  const [tenants, setTenants]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null); // null | 'create' | 'edit' | 'password' | 'invite'
  const [selected, setSelected]   = useState(null);
  const [saving, setSaving]       = useState(false);

  const [form, setForm] = useState({
    username: '', email: '', password: '', role: 'user', tenantId: '',
  });
  const [pwdForm, setPwdForm]     = useState({ newPassword: '', confirm: '' });
  const [inviteForm, setInvite]   = useState({ email: '', role: 'user' });
  const [inviteResult, setInviteResult] = useState(null); // { inviteUrl, message }

  // ── Carga ────────────────────────────────────────────────────────────────

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/users');
      setUsers(data || []);
    } catch {
      addNotification('Error al cargar los usuarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  // Cargar tenants si es superadmin
  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/api/tenants').then(data => setTenants(data || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  // ── Filtro local ─────────────────────────────────────────────────────────

  const visibleUsers = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // ── Helpers de modal ─────────────────────────────────────────────────────

  const openCreate = () => {
    setForm({ username: '', email: '', password: '', role: 'user', tenantId: isSuperAdmin ? '' : '' });
    setSelected(null);
    setModal('create');
  };

  const openEdit = (u) => {
    setForm({ username: u.username, email: u.email, password: '', role: u.role, tenantId: u.tenantId });
    setSelected(u);
    setModal('edit');
  };

  const openPassword = (u) => {
    setPwdForm({ newPassword: '', confirm: '' });
    setSelected(u);
    setModal('password');
  };

  const closeModal = () => { setModal(null); setSelected(null); setInviteResult(null); };

  const handleInvite = async () => {
    if (!inviteForm.email) { addNotification('Ingresa un email', 'warning'); return; }
    setSaving(true);
    try {
      const data = await api.post('/api/invitations', inviteForm);
      setInviteResult({ inviteUrl: data.inviteUrl, message: data.message });
    } catch (err) {
      addNotification(err.message || 'Error al enviar la invitación', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Acciones ─────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!form.username || !form.email || !form.password) {
      addNotification('Completa todos los campos requeridos', 'warning');
      return;
    }
    if (isSuperAdmin && !form.tenantId) {
      addNotification('Selecciona una empresa para el usuario', 'warning');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/users', {
        username: form.username,
        email:    form.email,
        password: form.password,
        role:     form.role,
        tenantId: isSuperAdmin ? parseInt(form.tenantId) : currentUser.tenantId,
      });
      addNotification('Usuario creado correctamente', 'success');
      closeModal();
      loadUsers();
    } catch (err) {
      addNotification(err?.detail || 'Error al crear el usuario', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.email) {
      addNotification('El correo es requerido', 'warning');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/users/${selected.id}`, {
        email: form.email,
        role:  form.role,
      });
      addNotification('Usuario actualizado', 'success');
      closeModal();
      loadUsers();
    } catch (err) {
      addNotification(err?.detail || 'Error al actualizar el usuario', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePassword = async () => {
    if (pwdForm.newPassword.length < 6) {
      addNotification('La contraseña debe tener al menos 6 caracteres', 'warning');
      return;
    }
    if (pwdForm.newPassword !== pwdForm.confirm) {
      addNotification('Las contraseñas no coinciden', 'warning');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/users/${selected.id}/password`, {
        newPassword: pwdForm.newPassword,
      });
      addNotification('Contraseña actualizada', 'success');
      closeModal();
    } catch (err) {
      addNotification(err?.detail || 'Error al cambiar la contraseña', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.patch(`/api/users/${u.id}/active`, { isActive: !u.isActive });
      addNotification(`Usuario ${!u.isActive ? 'activado' : 'desactivado'}`, 'success');
      loadUsers();
    } catch {
      addNotification('Error al cambiar el estado del usuario', 'error');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text)]">Usuarios</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">
            {isSuperAdmin ? 'Todos los tenants' : 'Tu empresa'}
          </p>
        </div>
        <div className="flex gap-2">
          <BaseButton variant="outline" icon={EnvelopeSimple}
            onClick={() => { setInvite({ email: '', role: 'user' }); setInviteResult(null); setModal('invite'); }}>
            Invitar
          </BaseButton>
          <BaseButton icon={Plus} onClick={openCreate}>Nuevo usuario</BaseButton>
        </div>
      </div>

      {/* Buscador */}
      <div className="relative max-w-sm">
        <MagnifyingGlass
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"
        />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por usuario o correo…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--border)]
                     bg-[var(--surface)] text-[var(--text)] placeholder:text-[var(--text-tertiary)]
                     focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
        />
      </div>

      {/* Tabla */}
      <BaseCard>
        {loading ? (
          <p className="text-sm text-[var(--text-secondary)] p-4">Cargando usuarios…</p>
        ) : visibleUsers.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)] p-4">No se encontraron usuarios.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-secondary)]">
                  <th className="text-left py-3 px-4 font-medium">Usuario</th>
                  <th className="text-left py-3 px-4 font-medium">Correo</th>
                  <th className="text-left py-3 px-4 font-medium">Rol</th>
                  {isSuperAdmin && <th className="text-left py-3 px-4 font-medium">Empresa</th>}
                  <th className="text-left py-3 px-4 font-medium">Estado</th>
                  <th className="text-right py-3 px-4 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map(u => (
                  <tr
                    key={u.id}
                    className="border-b border-[var(--border-subtle)] hover:bg-[var(--surface-raised)] transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <UserCircle size={20} className="text-[var(--text-tertiary)] shrink-0" />
                        <span className="font-medium text-[var(--text)]">{u.username}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[var(--text-secondary)]">{u.email || '—'}</td>
                    <td className="py-3 px-4">
                      <RoleBadge role={u.role} />
                    </td>
                    {isSuperAdmin && (
                      <td className="py-3 px-4 text-[var(--text-secondary)]">
                        {u.tenantName || u.tenantId}
                      </td>
                    )}
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.isActive
                          ? 'bg-success-bg text-success-text'
                          : 'bg-[var(--surface-raised)] text-[var(--text-tertiary)]'
                      }`}>
                        {u.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(u)}
                          title="Editar"
                          className="p-1.5 rounded hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]"
                        >
                          <PencilSimple size={15} />
                        </button>
                        <button
                          onClick={() => openPassword(u)}
                          title="Cambiar contraseña"
                          className="p-1.5 rounded hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]"
                        >
                          <LockSimple size={15} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(u)}
                          title={u.isActive ? 'Desactivar' : 'Activar'}
                          className={`p-1.5 rounded hover:bg-[var(--surface-raised)] ${
                            u.isActive ? 'text-error' : 'text-success-text'
                          }`}
                        >
                          <Power size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </BaseCard>

      {/* Modal crear / editar */}
      {(modal === 'create' || modal === 'edit') && (
        <ModalOverlay onClose={closeModal}>
          <h2 className="text-lg font-semibold text-[var(--text)] mb-4">
            {modal === 'create' ? 'Nuevo usuario' : 'Editar usuario'}
          </h2>

          <div className="space-y-3">
            {modal === 'create' && (
              <BaseInput
                label="Usuario *"
                value={form.username}
                onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                placeholder="juan.perez"
              />
            )}
            {modal === 'edit' && (
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Usuario
                </label>
                <p className="text-sm text-[var(--text)] font-medium">{form.username}</p>
              </div>
            )}

            <BaseInput
              label="Correo electrónico *"
              type="email"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="juan@empresa.com"
            />

            {modal === 'create' && (
              <BaseInput
                label="Contraseña *"
                type="password"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Mínimo 6 caracteres"
              />
            )}

            {isSuperAdmin && modal === 'create' && (
              <div>
                <label htmlFor="user-modal-tenant" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Empresa *
                </label>
                <select
                  id="user-modal-tenant"
                  value={form.tenantId}
                  onChange={e => setForm(p => ({ ...p, tenantId: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border)]
                             bg-[var(--surface)] text-[var(--text)]
                             focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                >
                  <option value="">Seleccionar empresa…</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label htmlFor="user-modal-role" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Rol *
              </label>
              <select
                id="user-modal-role"
                value={form.role}
                onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border)]
                           bg-[var(--surface)] text-[var(--text)]
                           focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
              >
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <BaseButton variant="outline" onClick={closeModal}>Cancelar</BaseButton>
            <BaseButton
              onClick={modal === 'create' ? handleCreate : handleUpdate}
              disabled={saving}
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </BaseButton>
          </div>
        </ModalOverlay>
      )}

      {/* Modal cambio de contraseña */}
      {modal === 'password' && (
        <ModalOverlay onClose={closeModal}>
          <h2 className="text-lg font-semibold text-[var(--text)] mb-1">
            Cambiar contraseña
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Usuario: <strong>{selected?.username}</strong>
          </p>

          <div className="space-y-3">
            <BaseInput
              label="Nueva contraseña *"
              type="password"
              value={pwdForm.newPassword}
              onChange={e => setPwdForm(p => ({ ...p, newPassword: e.target.value }))}
              placeholder="Mínimo 6 caracteres"
            />
            <BaseInput
              label="Confirmar contraseña *"
              type="password"
              value={pwdForm.confirm}
              onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value }))}
              placeholder="Repite la contraseña"
            />
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <BaseButton variant="outline" onClick={closeModal}>Cancelar</BaseButton>
            <BaseButton onClick={handlePassword} disabled={saving}>
              {saving ? 'Guardando…' : 'Cambiar contraseña'}
            </BaseButton>
          </div>
        </ModalOverlay>
      )}

      {/* Modal invitación */}
      {modal === 'invite' && (
        <ModalOverlay title="Invitar usuario por email" onClose={closeModal}>
          {!inviteResult ? (
            <>
              <div className="space-y-3">
                <BaseInput
                  label="Email del invitado *"
                  type="email"
                  value={inviteForm.email}
                  onChange={e => setInvite(f => ({ ...f, email: e.target.value }))}
                  placeholder="correo@empresa.com"
                />
                <div>
                  <label htmlFor="invite-modal-role" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Rol</label>
                  <select
                    id="invite-modal-role"
                    value={inviteForm.role}
                    onChange={e => setInvite(f => ({ ...f, role: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--text)]">
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <BaseButton variant="outline" onClick={closeModal}>Cancelar</BaseButton>
                <BaseButton icon={EnvelopeSimple} onClick={handleInvite} disabled={saving}>
                  {saving ? 'Enviando…' : 'Enviar invitación'}
                </BaseButton>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'var(--text)' }}>{inviteResult.message}</p>
              {inviteResult.inviteUrl && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    Link de invitación (válido 48h):
                  </p>
                  <div className="flex gap-2 items-center">
                    <input
                      readOnly
                      value={inviteResult.inviteUrl}
                      className="flex-1 text-xs px-3 py-2 rounded-lg border"
                      style={{ background: 'var(--surface-raised)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    />
                    <BaseButton size="sm" variant="outline"
                      onClick={() => { navigator.clipboard.writeText(inviteResult.inviteUrl); addNotification('Link copiado', 'success'); }}>
                      Copiar
                    </BaseButton>
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <BaseButton onClick={closeModal}>Cerrar</BaseButton>
              </div>
            </div>
          )}
        </ModalOverlay>
      )}
    </div>
  );
};

// ── Sub-componentes ───────────────────────────────────────────────────────────

const RoleBadge = ({ role }) => {
  const styles = {
    superadmin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    admin:      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    user:       'bg-[var(--surface-raised)] text-[var(--text-secondary)]',
  };
  const labels = { superadmin: 'Super Admin', admin: 'Admin', user: 'Usuario' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || styles.user}`}>
      {labels[role] || role}
    </span>
  );
};

const ModalOverlay = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="bg-[var(--surface)] rounded-xl shadow-xl w-full max-w-md p-6 relative">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-[var(--text-tertiary)] hover:text-[var(--text)]"
      >
        <X size={18} />
      </button>
      {children}
    </div>
  </div>
);

export default UsersModule;
